/* eslint-disable no-undef */
import axios from "axios";
import {
  coinbasePriceUrl,
  coingeckoListUrl,
  coingeckoPriceUrl,
  currenciesToFiat,
  emailSenderUrl,
  errorToast,
  PoolType,
  verifiedBrokerageUrl,
} from "../../constants";
import { PrimaryIssuePool, SecondaryIssuePool } from "../../../lib/sor/index";
import { BigNumber, ethers } from "ethers";
import Web3 from "web3";
import { ChainConfig, chainDetails } from "../../config";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { contractAddress } from "@verified-network/verified-sdk";
import ERC20ABI from "../../abis/ERC20.json";
import COMETABI from "../../abis/Comet.json";
import AddFunds from "../../../components/popups/addFunds";
import SuccessPopup from "../../../components/popups/success";
import bs58 from "bs58";
import { keccak256 } from "viem";

export const capitalizeFirstLetter = (content) => {
  if (content.length === 0) return "";
  return content.charAt(0).toUpperCase() + content.slice(1).toLowerCase();
};

export const formatBytes32SafeText = (text) => {
  const maxBytes = 31;

  let truncated = text;

  while (ethers.utils.toUtf8Bytes(truncated).length > maxBytes) {
    truncated = truncated.slice(0, -1);
  }

  return truncated;
};

export const convertToBlockTimestamp = (day, month, year) => {
  const date = new Date(year, month - 1, day);
  return Math.floor(date.getTime() / 1000);
};

export const convertFromBlockTimestamp = (blockTimestamp, showTime) => {
  const unixTime = new Date(Number(blockTimestamp) * 1000);
  if (showTime) {
    const day = unixTime.getDate();
    const suffixes = ["th", "st", "nd", "rd"];
    const suffix =
      day % 10 > 3 || Math.floor(day / 10) === 1 ? "th" : suffixes[day % 10];
    return `${day}${suffix} ${unixTime.toLocaleString("en-US", {
      month: "short",
    })}, ${unixTime.getFullYear()} - ${unixTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  }
  return unixTime.toLocaleDateString("default");
};

export const getYesterdayTimeFromNow = () => {
  const now = new Date();
  const nowInMinutes = now.getTime();
  const yesterday = new Date(nowInMinutes - 24 * 60 * 60 * 1000); //24hours in minutes
  return yesterday.getTime();
};

export const get2DaysBackTimeFromNow = () => {
  const now = new Date();
  const nowInMinutes = now.getTime();
  const yesterday = new Date(nowInMinutes - 48 * 60 * 60 * 1000); //24hours in minutes
  return yesterday.getTime();
};

export const getTimeValueInSeconds = (timeValue, timeType) => {
  const value = Number(timeValue);
  if (isNaN(value) || value < 0) return 0;

  const unit = timeType?.toLowerCase();

  switch (unit) {
    case "day":
    case "days":
      return value * 86400;

    case "month":
    case "months":
      return value * 2628000; // 30.44 days average

    case "year":
    case "years":
      return value * 31536000;

    default:
      return 0;
  }
};

export const fetchMarginPoolCurrentPrice = async (
  currencySymbol,
  securitySymbol,
  poolCfiCode,
  priceType,
) => {
  const marginCurrencySymbol = currenciesToFiat[currencySymbol]
    ? currenciesToFiat[currencySymbol]
    : "USD";
  const brokerageApiEndpoint = process.env.REACT_APP_BROKERAGE_ENDPOINT;
  const url = brokerageApiEndpoint
    ? `${brokerageApiEndpoint}/${poolCfiCode.toLowerCase()}?security=${securitySymbol}&cash=${marginCurrencySymbol}&type=${priceType}`
    : "";
  if (url) {
    return await axios({
      method: "GET",
      url: url,
    })
      .then((res) => {
        return res?.data?.body?.price;
      })
      .catch(async (err) => {
        console.error(
          `Request to get response from url: ${url} failed with error: ${err?.message}`,
        );
        return await axios({
          method: "GET",
          url: currenciesToFiat[currencySymbol]
            ? `https://api.coinbase.com/v2/prices/${securitySymbol}-${currenciesToFiat[currencySymbol]}/spot`
            : `https://api.coinbase.com/v2/prices/${securitySymbol}-USD/spot`,
        })
          .then((res) => {
            const response = res.data;
            return response.data.amount;
          })
          .catch((err) => {
            console.log(
              `Request to get response from url: ${url} failed with error: ${err?.message}`,
            );
            return null;
          });
      });
  } else {
    console.error("Brokarage api endpoint not found. Try again later");
    return null;
  }
};

export const getCompoundYearlyRateInFraction = async (web3, cometAddress) => {
  const cometContract = new web3.eth.Contract(COMETABI, cometAddress);
  const utilization = await cometContract.methods.getUtilization().call();
  const borrowRate = await cometContract.methods
    .getBorrowRate(Number(utilization))
    .call();
  return 31536000 * Number(ethers.utils.formatEther(borrowRate));
};

export const formatFileSize = (sizeInBytes) => {
  let formattedSize;
  if (Number(sizeInBytes) < 1024)
    formattedSize = `${sizeInBytes} B`; //not up to KB ??
  else if (Number(sizeInBytes) < 1048576)
    formattedSize = `${(Number(sizeInBytes) / 1024).toFixed(2)} KB`;
  // noot up to MB ??
  else formattedSize = `${(Number(sizeInBytes) / 1048576).toFixed(2)} MB`;

  return formattedSize;
};

export const extractNumberWithCommas = (textInput) => {
  let numberWithDecimals = textInput.toString().replace(/[^0-9.]/g, "");
  if (numberWithDecimals.includes(".")) {
    const [integer, decimal] = numberWithDecimals.split(".");
    const integerWithCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return {
      number: Number(`${integer}.${decimal}`),
      fmtNumber: `${integerWithCommas}.${decimal}`,
    };
  } else {
    return {
      number: Number(numberWithDecimals),
      fmtNumber: numberWithDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    };
  }
};

export const generateSecureCloidHyper = (orderId, isStoploss) => {
  const standardizedInput = orderId.startsWith("0x") ? orderId : `0x${orderId}`;

  const fullHash = keccak256(
    isStoploss
      ? `${standardizedInput}${ethers.utils.formatBytes32String("STOP")}`
      : standardizedInput,
  );

  return fullHash.slice(0, 34);
};

const requestQueues = new Map();
export const rateLimitedRequest = async (key, fn, ttl = 1000) => {
  const previous = requestQueues.get(key) || Promise.resolve();

  const next = previous.then(async () => {
    await new Promise((resolve) => setTimeout(resolve, ttl));

    return fn();
  });

  requestQueues.set(
    key,
    next.catch(() => {}),
  );

  return next;
};

const calculateCollectedFee = (size, price, feeRatePercentage) => {
  const notionalFloat = Number(size) * Number(price);
  const notionalSubunits = BigInt(
    ethers.utils.parseUnits(notionalFloat.toFixed(6), "6"),
  );

  const SCALE_FACTOR = 1_000_000n;

  const feeRateInteger = BigInt(
    Math.round(Number(feeRatePercentage) * Number(SCALE_FACTOR)),
  );

  return (notionalSubunits * feeRateInteger) / SCALE_FACTOR;
};

let cachedFeeSchedule = null;
let lastFetchTime = 0;
let isFetching = false;

const CACHE_TTL_MS = 1 * 60 * 1000; //1 minute.

export const getAndCalculateLiveFeesHyper = async (
  chainId,
  tradeSize,
  tradePrice,
  isMarketOrder,
  isStoploss,
) => {
  const isProd = process.env.REACT_APP_NODE_ENV === "production";
  const url = isProd
    ? "https://api.hyperliquid.xyz/info"
    : "https://api.hyperliquid-testnet.xyz/info";

  const userAddress = chainDetails[chainId]?.brokerages?.HYPERLIQUID;
  if (!userAddress) {
    throw new Error(
      `No brokerage address found for chainId: ${chainId} and no cache available.`,
    );
  }

  const now = Date.now();
  const isCacheExpired = now - lastFetchTime > CACHE_TTL_MS;

  //send request if the cache is expired and no request running
  if ((isCacheExpired || !cachedFeeSchedule) && !isFetching) {
    isFetching = true;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "userFees",
          user: userAddress,
        }),
      });

      if (!response.ok) {
        throw new Error(`Hyperliquid API status ${response.status}`);
      }

      const data = await response.json();

      if (data?.feeSchedule) {
        cachedFeeSchedule = data.feeSchedule;
        lastFetchTime = now;
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      console.error(
        "Hyperliquid API fetch failed, falling back to cache:",
        error.message,
      );
    } finally {
      isFetching = false;
    }
  }

  const scheduleToUse = cachedFeeSchedule || { cross: "0", add: "0" };

  return calculateHyperFeeFromSchedule(
    scheduleToUse,
    tradeSize,
    tradePrice,
    isMarketOrder,
  );
};

const calculateHyperFeeFromSchedule = (
  feeSchedule,
  tradeSize,
  tradePrice,
  isMarketOrder,
) => {
  const activeRateToUse = isMarketOrder ? feeSchedule.cross : feeSchedule.add;

  const dynamicFeeInSubunits = calculateCollectedFee(
    tradeSize,
    tradePrice,
    activeRateToUse,
  );

  return Number(
    ethers.utils.formatUnits(dynamicFeeInSubunits?.toString(), "6"),
  );
};

export const calculateHyperliquidEntryPrice = (orderFills) => {
  let totalNotional = 0;
  let totalSize = 0;

  orderFills.forEach((fill) => {
    const price = parseFloat(fill.px);
    const size = parseFloat(fill.sz);

    const notional = price * size;

    totalNotional += notional;
    totalSize += size;
  });

  if (totalSize === 0) return 0;

  return totalNotional / totalSize;
};

export const calculateMaxFastTransferAndProtocolFee = async (
  sourceDomain,
  destDomain,
  usdAmountInEther,
  includeHyperCoreForwardingFee,
) => {
  const isProd = process.env.REACT_APP_NODE_ENV === "production";
  const [whole, decimal = ""] = usdAmountInEther.split(".");
  const decimal6 = (decimal + "000000").slice(0, 6);
  const transferAmount = BigInt(whole + decimal6);

  const response = await fetch(
    `${isProd ? "https://iris-api.circle.com" : "https://iris-api-sandbox.circle.com"}/v2/burn/USDC/fees/${sourceDomain}/${destDomain}?forward=true`,
  );
  const fees = await response.json();

  // Fast Transfer
  const minimumFee = fees[0].minimumFee; // Fee in basis points

  // Calculate fee as percentage of transfer amount
  const protocolFee =
    (transferAmount * BigInt(Math.round(minimumFee * 100))) / 1_000_000n;

  // Add 20% buffer to protocol fee (protocolFee × 1.2) - result in subunits
  const maxFee = (protocolFee * 120n) / 100n;

  return includeHyperCoreForwardingFee
    ? Number(ethers.utils.formatUnits((maxFee + 200_000n)?.toString(), "6"))
    : Number(ethers.utils.formatUnits(maxFee?.toString(), "6"));
};

export const formatNumberWithUnits = (numberToFormat, decimals) => {
  if (isNaN(Number(numberToFormat))) {
    return "0.00";
  }
  const units = ["", "K", "M", "B", "T", "P"];
  let unitIdx = 0;

  for (
    numberToFormat;
    Number(numberToFormat) >= 1000 && unitIdx < units.length - 1;
    unitIdx++
  ) {
    numberToFormat = Number(numberToFormat) / 1000;
  }

  if (decimals === undefined) {
    return Number(numberToFormat).toFixed(2) + units[unitIdx];
  } else if (Number(decimals) === 0) {
    return numberToFormat + units[unitIdx];
  } else if (Number(decimals) > 0) {
    return Number(numberToFormat).toFixed(decimals) + units[unitIdx];
  }
};

export const formatTimeWithUnits = (timeInSeconds) => {
  const units = {
    year: 365 * 24 * 60 * 60,
    month: 30 * 24 * 60 * 60,
    day: 24 * 60 * 60,
  };

  const result = {};

  for (const [unit, value] of Object.entries(units)) {
    result[unit] = Math.floor(timeInSeconds / value);
    timeInSeconds %= value;
  }

  const parts = Object.entries(result)
    .filter(([_, val]) => val > 0)
    .map(([unit, val]) => `${val} ${unit}${val > 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(" ") : "0 days";
};

export const calculateRepaymentInterest = (amount, apy, tenureInSeconds) => {
  const calenderYearInSeconds = 365 * 24 * 60 * 60;
  const interest =
    Number(amount) *
    Number(apy) *
    (Number(tenureInSeconds) / calenderYearInSeconds);
  return interest;
};

export const switchNetwork = async (chainId, window) => {
  try {
    if (!window.ethereum) {
      errorToast(
        "Opps something went wrong.",
        "Download metamask and try again.",
      );
      return false;
    }
    return await window.ethereum
      .request({
        method: "wallet_addEthereumChain",
        params: [
          {
            ...ChainConfig[chainId],
          },
        ],
      })
      .then((res) => {
        return true;
      });
  } catch (err) {
    console.error("switch network failed: ", err);
    errorToast("Opps something went wrong.", "Approve switch network request.");
    return false;
  }
};

export const convertFromTimeStamp = (timeStamp) => {
  return new Date(timeStamp).toLocaleString("default", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const fetchSupportedTokensCoingecko = async () => {
  return await axios({
    url: coingeckoListUrl,
    method: "GET",
    headers: {
      Accept: "application/json",
      x_cg_demo_api_key: process.env.REACT_APP_COINGECKO_API_KEY,
    },
  })
    .then((res) => {
      if (res?.data) {
        return res?.data;
      } else {
        return [];
      }
    })
    .catch((err) => {
      return [];
    });
};

export const fetchTokenPriceCoingecko = async (
  tokenFromCoingeckoId,
  tokenToSymbol,
) => {
  if (tokenFromCoingeckoId && tokenToSymbol) {
    return await axios({
      url: `${coingeckoPriceUrl}?ids=${tokenFromCoingeckoId}&vs_currencies=${tokenToSymbol?.toLowerCase()}&x_cg_demo_api_key=${
        process.env.REACT_APP_COINGECKO_API_KEY
      }`,
      method: "GET",
      headers: {
        Accept: "application/json",
        x_cg_demo_api_key: process.env.REACT_APP_COINGECKO_API_KEY,
      },
    })
      .then((res) => {
        if (res?.data) {
          return res?.data[tokenFromCoingeckoId][tokenToSymbol?.toLowerCase()];
        } else {
          return "0";
        }
      })
      .catch((err) => {
        return "0";
      });
  } else {
    return "0";
  }
};

export const fetchTokenPriceCoinbase = async (
  securitySymbol,
  currencyFiatSymbol,
) => {
  return await axios({
    method: "GET",
    url: `${coinbasePriceUrl}/${securitySymbol?.toUpperCase()}-${currencyFiatSymbol?.toUpperCase()}/spot`,
  })
    .then((res) => {
      if (res?.data?.data.amount) {
        return res?.data?.data.amount;
      } else {
        return "0";
      }
    })
    .catch((err) => {
      return "0";
    });
};

export const fetchTokenPriceFromVerified = async (
  cfiCode,
  securitySymbol,
  currencyFiatSymbol,
  priceType,
) => {
  if (cfiCode === "GCEX") {
    return "0";
  }
  // if (cfiCode === "HYPERLIQUID") {
  //   const isProd = process.env.REACT_APP_NODE_ENV === "production";
  //   const url = isProd
  //     ? "https://api.hyperliquid.xyz/info"
  //     : "https://api.hyperliquid-testnet.xyz/info";
  //   const response = await fetch(url, {
  //     method: "POST",
  //     headers: { "Content-Type": "application/json" },
  //     body: JSON.stringify({
  //       type: "allMids",
  //     }),
  //   });

  //   if (!response.ok) {
  //     return "0";
  //   }

  //   const data = await response.json();
  //   return data;
  // }
  const url = `${verifiedBrokerageUrl}/${cfiCode.toLowerCase()}?security=${securitySymbol?.toUpperCase()}&cash=${currencyFiatSymbol?.toUpperCase()}&type=${
    priceType?.toUpperCase() || "BUY"
  }`;
  return await axios({
    method: "GET",
    url: url,
  })
    .then((res) => {
      if (res?.data?.body?.price) {
        return res?.data?.body?.price;
      } else {
        return "0";
      }
    })
    .catch((err) => {
      console.error(
        `Error while fetching price for ${securitySymbol?.toUpperCase()}-${currencyFiatSymbol?.toUpperCase()} on ${cfiCode.toLowerCase()} endpoint: ${
          err?.message
        }`,
      );
      return "0";
    });
};

export const fetchTokenPriceFromVerifiedSpot = async (
  cfiCode,
  securitySymbol,
  currencyFiatSymbol,
  priceType,
) => {
  const url = `${verifiedBrokerageUrl}/${cfiCode.toLowerCase()}/spot?security=${securitySymbol?.toUpperCase()}&cash=${currencyFiatSymbol?.toUpperCase()}&type=${
    priceType?.toUpperCase() || "BUY"
  }`;
  return await axios({
    method: "GET",
    url: url,
  })
    .then((res) => {
      if (res?.data?.body?.price) {
        return res?.data?.body?.price;
      } else {
        return "0";
      }
    })
    .catch((err) => {
      console.error(
        `Error while fetching price for ${securitySymbol?.toUpperCase()}-${currencyFiatSymbol?.toUpperCase()} on ${cfiCode.toLowerCase()} endpoint: ${
          err?.message
        }`,
      );
      return "0";
    });
};

export const formatNumberAsPrice = (numberToformat) => {
  if (Number(numberToformat) === 0) return "0";

  if (Math.abs(Number(numberToformat)) >= 0.0001) {
    return Number(numberToformat).toFixed(4);
  }

  // For very small numbers, convert to string in full form
  const parts = Number(numberToformat).toExponential().split("e");
  const base = parseFloat(parts[0]).toString().replace(".", "");
  const exponent = parseInt(parts[1]);

  const zeros = "0.".padEnd(Math.abs(exponent) + 2, "0");
  return zeros + base;
};

export const getAllVerifiedCashBalances = async (
  chainId,
  web3,
  userAddress,
) => {
  const cashTokens = contractAddress[chainId]?.CASH;
  if (cashTokens && Object.values(cashTokens)?.length > 0) {
    const verifiedCashFmt = await Promise.all(
      Object.keys(cashTokens)?.map(async (tkNm) => {
        if (cashTokens[tkNm]?.length > 0) {
          const cashTokenContract = new web3.eth.Contract(
            ERC20ABI,
            cashTokens[tkNm],
          );
          const balance = await cashTokenContract.methods
            .balanceOf(userAddress)
            .call();
          return {
            name: tkNm,
            address: cashTokens[tkNm],
            balance: Number(balance) > 0 ? Number(balance) : 0,
            balanceFmt:
              Number(balance) > 0
                ? Number(ethers.utils.formatEther(Number(balance)))
                : 0,
          };
        } else {
          return null;
        }
      }),
    );

    const validverifiedCash = verifiedCashFmt?.filter((csh) => csh !== null);

    const validverifiedCashFmt = validverifiedCash?.sort(
      (a, b) => b.balance - a.balance,
    );

    if (
      (validverifiedCashFmt[0]?.balance === 0 &&
        chainDetails[chainId]?.usdc?.length > 0) ||
      validverifiedCashFmt?.length === 0
    ) {
      const usdcContract = new web3.eth.Contract(
        ERC20ABI,
        chainDetails[chainId]?.usdc,
      );
      const balance = await usdcContract.methods.balanceOf(userAddress).call();

      if (validverifiedCashFmt?.length > 0) {
        validverifiedCashFmt[1] = validverifiedCashFmt[0]; // go 1 back to keep the prev highest after USDC???
      }

      validverifiedCashFmt[0] = {
        name: "USDC",
        address: chainDetails[chainId]?.usdc,
        balance: Number(balance) > 0 ? Number(balance) : 0,
        balanceFmt:
          Number(balance) > 0
            ? Number(ethers.utils.formatUnits(Number(balance), "6"))
            : 0,
      };
    }

    return validverifiedCashFmt;
  } else if (chainDetails[chainId]?.usdc && web3) {
    const usdcContract = new web3.eth.Contract(
      ERC20ABI,
      chainDetails[chainId]?.usdc,
    );
    const balance = await usdcContract.methods.balanceOf(userAddress).call();

    return [
      {
        name: "USDC",
        address: chainDetails[chainId]?.usdc,
        balance: Number(balance) > 0 ? Number(balance) : 0,
        balanceFmt:
          Number(balance) > 0
            ? Number(ethers.utils.formatUnits(Number(balance), "6"))
            : 0,
      },
    ];
  } else {
    return;
  }
};

export const authenticatePaymentToken = async (
  chainId,
  userAddress,
  web3,
  verifiedWalletDetails,
  contract,
  functionName,
  functionArgs,
  helpers,
  isMobileScreen,
) => {
  let paymentToken;

  if (
    verifiedWalletDetails &&
    Object?.keys(verifiedWalletDetails)?.length > 0
  ) {
    const cashTokens = await getAllVerifiedCashBalances(
      chainId,
      web3,
      userAddress,
    );

    if (!cashTokens || cashTokens?.length === 0) {
      helpers?.setCustomPopupPosition?.(isMobileScreen ? "center" : "flex-end");
      helpers?.setCustomPopupContent?.(
        <SuccessPopup
          setShowPopup={helpers?.setShowPopup}
          setPopupContent={helpers?.setPopupContent}
          setPopupContentPosition={helpers?.setPopupContentPosition}
          isCustom={true}
          customTextHeader="Transaction Failed!"
          customTextFooter="Something went wrong"
          setFavouriteClicked={helpers?.setFavouriteClicked}
          isError={true}
          setCustomPopupPosition={helpers?.setCustomPopupPosition}
          setCustomPopupContent={helpers?.setCustomPopupContent}
          setShowCustomPopup={helpers?.setShowCustomPopup}
        />,
      );
      helpers?.setShowCustomPopup?.(true);
      errorToast(
        "Unexpected Error",
        "No valid Cash Token on this network. Change network and try again.",
      );

      return;
    }
    paymentToken = cashTokens[0];
    const txQuote = await contract._getMeeQuote(
      cashTokens[0]?.address,
      functionName,
      functionArgs,
    );

    if (
      Number(cashTokens[0]?.balance) === 0 ||
      Number(txQuote?.amount) > Number(cashTokens[0]?.balance)
    ) {
      helpers?.setCustomPopupPosition?.(isMobileScreen ? "center" : "flex-end");
      helpers?.setCustomPopupContent?.(
        <SuccessPopup
          setShowPopup={helpers?.setShowPopup}
          setPopupContent={helpers?.setPopupContent}
          setPopupContentPosition={helpers?.setPopupContentPosition}
          isCustom={true}
          customTextHeader="Transaction Failed!"
          customTextFooter={`Insufficient ${cashTokens[0]?.name} to pay for transaction`}
          setFavouriteClicked={helpers?.setFavouriteClicked}
          isError={true}
          setCustomPopupPosition={helpers?.setCustomPopupPosition}
          setCustomPopupContent={helpers?.setCustomPopupContent}
          setShowCustomPopup={helpers?.setShowCustomPopup}
          showButton={true}
          buttonText={`Topup ${cashTokens[0]?.name}`}
          buttonFunc={() => {
            if (isMobileScreen) {
              helpers.setPageAction?.("add-funds");
              helpers?.navigate?.("/add-funds");
            } else {
              const input = document.getElementById("add-funds-popup-input");
              if (input) {
                input.value = "";
              }
              helpers?.setSelectedCurrency?.({
                countryName: "",
                officialName: "",
                flag: null,
                currencySymbol: "",
                currencyName: "",
                officialCurrencySymbol: "",
                isClicked: false,
                balance: 0.0,
                isCrypto: true,
              });
              helpers?.setAmount?.("");

              helpers?.setPopupContent?.(null);
              helpers?.setPopupContentPosition?.("flex-end");
              helpers?.setPopupContent?.(
                <AddFunds
                  setShowPopup={helpers?.setShowPopup}
                  setPopupContent={helpers?.setPopupContent}
                  setPopupContentPosition={helpers?.setPopupContentPosition}
                  customLoading={helpers?.customLoading}
                  setCustomLoading={helpers?.setCustomLoading}
                  generalDataProps={helpers?.generalDataProps}
                  addFundsAction="add-funds"
                />,
              );
              helpers?.setShowPopup(true);
            }
          }}
        />,
      );
      helpers?.setShowCustomPopup?.(true);
      errorToast(
        `Insufficient ${cashTokens[0]?.name} balance`,
        `You need at least: ${txQuote?.amount}  ${cashTokens[0]?.name} to proceed.`,
      );
    }
  }

  return paymentToken;
};

export const uploadProductAndSiteMap = async (products, chainId) => {
  try {
    const res = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_SITEMAP_ENDPOINT}/update-sitemap`,
      data: {
        products,
        chainId,
      },
    });
    if (res?.data) {
      return true;
    } else {
      return false;
    }
  } catch (err) {
    return false;
  }
};

export const showCustomAlert = (targetElement, message) => {
  const rect = targetElement.getBoundingClientRect();
  const alert = document.createElement("div");

  alert.className = "verified-custd-ext-alert";
  alert.textContent = message;

  Object.assign(alert.style, {
    position: "fixed",
    top: `${rect.top - 30}px`,
    left: `${rect.left + rect.width / 2}px`,
    transform: "translateX(-50%) translateY(5px)",
    background: "var(--secondary-electric-violet-600, #603ff4)",
    color: "#fff",
    padding: "4px 8px",
    fontSize: "12px",
    borderRadius: "6px",
    boxShadow: "0 1px 3px rgba(27,31,35,0.15)",
    opacity: "0",
    transition: "opacity 0.2s ease-out, transform 0.2s ease-out",
    zIndex: "9999",
    pointerEvents: "none",
  });

  document.body.appendChild(alert);

  requestAnimationFrame(() => {
    alert.style.opacity = "1";
    alert.style.transform = "translateX(-50%) translateY(0)";
  });

  setTimeout(() => {
    alert.style.opacity = "0";
    alert.style.transform = "translateX(-50%) translateY(-5px)";
    setTimeout(() => alert.remove(), 300);
  }, 2000);
};

export const hexToBytes = (hex) => {
  // Remove 0x prefix if present
  const cleanHex =
    hex.startsWith("0x") || hex.startsWith("0X") ? hex.slice(2) : hex;

  if (cleanHex.length % 2 !== 0) {
    throw new Error("Invalid hex string: odd length");
  }

  const byteLength = cleanHex.length / 2;
  const bytes = new Uint8Array(byteLength);
  for (let i = 0; i < byteLength; i++) {
    const byteHex = cleanHex.substr(i * 2, 2);
    bytes[i] = parseInt(byteHex, 16);
  }
  return bytes;
};

export const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const flattenArray = (arr) => {
  const result = [];

  for (const item of arr) {
    if (Array.isArray(item)) {
      if (item.length > 0) {
        result.push(...flattenArray(item));
      }
    } else {
      result.push(item);
    }
  }

  return result;
};

export const calculatePrimaryAndSecondarySOR = async (
  poolIn,
  amountIn,
  swapType,
  userAddress,
) => {
  let tokenIn, tokenOut;
  if (swapType === "sell") {
    tokenIn = poolIn.security;
    tokenOut = poolIn.currency;
  } else {
    tokenIn = poolIn.currency;
    tokenOut = poolIn.security;
  }
  const pool =
    poolIn.poolType === PoolType.primaryPool
      ? PrimaryIssuePool.fromPool(poolIn)
      : SecondaryIssuePool.fromPool(poolIn);
  const poolPairData = pool.parsePoolPairData(tokenIn, tokenOut);
  const amount = ethers.utils.parseUnits(
    amountIn.toString(),
    Number(poolPairData.decimalsIn),
  );
  let amountOut,
    slippage = 0;
  if (swapType === "sell") {
    amountOut = pool._exactTokenInForTokenOut(
      poolPairData,
      BigNumber.from(amount.toString()),
      userAddress,
    );
    slippage = pool._spotPriceAfterSwapExactTokenInForTokenOut(
      poolPairData,
      BigNumber.from(amount.toString()),
      userAddress,
    );
  } else if (swapType === "buy") {
    amountOut = pool._tokenInForExactTokenOut(
      poolPairData,
      swapType === "buy" && poolIn.poolType === PoolType.secondaryPool
        ? BigNumber.from(amountIn.toString())
        : BigNumber.from(
            Web3.utils.toWei(Number(amountIn).toString(), "ether"),
          ),
      userAddress,
    );
    slippage = pool._spotPriceAfterSwapTokenInForExactTokenOut(
      poolPairData,
      BigNumber.from(amount.toString()),
      userAddress,
    );
  }
  const slippagePercentage = (Number(slippage) * 100).toFixed(8);
  return {
    slippage: slippagePercentage,
    amountOut: amountOut,
  };
};

export const getObject = async (objectKey, bucket) => {
  const bareBonesS3 = new S3Client({
    region: "us-east-1",
    s3UseArnRegion: true,
    credentials: {
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY,
    },
  });
  const getCommand = new GetObjectCommand({
    Bucket: bucket,
    Key: objectKey,
  });
  try {
    const commandResponse = await bareBonesS3.send(getCommand);
    if (commandResponse && commandResponse.$metadata.httpStatusCode === 200) {
      return await commandResponse.Body.transformToString("utf-8");
    } else {
      bareBonesS3.destroy();
      return null;
    }
  } catch (err) {
    bareBonesS3.destroy();
    return null;
  }
};

export const gcexCreateSpotOrder = async (
  security,
  cash,
  type,
  amount,
  creatorWallet,
) => {
  return await axios({
    url: "https://gateway.verified.network/api/gcex/orders",
    method: "POST",
    data: {
      security: security,
      cash: cash,
      type: type,
      amount: amount,
      creatorWallet: creatorWallet,
    },
  })
    .then((res) => {
      return true;
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error("create spot trade failed: ", err?.message);
      }

      return false;
    });
};

export const encryptKycString = async (plainText) => {
  return await axios({
    method: "POST",
    url: emailSenderUrl,
    data: {
      type: "encrypt",
      plainText,
    },
  })
    .then((res) => {
      if (res?.data?.encrypted) {
        return res?.data?.encrypted;
      } else {
        return null;
      }
    })
    .catch((err) => {
      console.log("encrypting plainText failed with error: ", err?.message);
      return null;
    });
};

export const getKycChallenge = async (userAddress) => {
  return await axios({
    method: "POST",
    url: emailSenderUrl,
    data: {
      type: "kyc_challenge",
      walletAddress: userAddress,
    },
  })
    .then((res) => {
      if (res?.data?.message && res?.data?.nonce) {
        return {
          message: res?.data?.message,
          nonce: res?.data?.nonce,
        };
      } else {
        return null;
      }
    })
    .catch((err) => {
      console.log("get decrypt challenge failed with error: ", err);
      return null;
    });
};

export const decryptUserKycData = async (
  userAddress,
  chainId,
  chainName,
  signer,
  _signature,
  _nonce,
  _nearNounce,
) => {
  try {
    const kycChallenge = _signature
      ? "skip"
      : await getKycChallenge(userAddress);
    if (
      (kycChallenge?.message && kycChallenge?.nonce) ||
      kycChallenge === "skip"
    ) {
      const message = kycChallenge?.message;

      const nearNonce = _signature
        ? _nearNounce
        : Buffer.from(window.crypto.getRandomValues(new Uint8Array(32)));

      const signature = _signature
        ? _signature
        : chainName?.toLowerCase() === "solana"
          ? await signer?.signMessage({
              message: new TextEncoder().encode(message),
              account: signer?.account,
            })
          : chainName?.toLowerCase() === "near"
            ? await signer?.signMessage({
                message,
                nonce: nearNonce,
                recipient: userAddress,
              })
            : await signer.signMessage(message);

      if (signature) {
        return await axios({
          method: "POST",
          url: emailSenderUrl,
          data: {
            type: "decrypt_kyc",
            walletAddress: userAddress,
            publicKey:
              chainName?.toLowerCase() === "near"
                ? `${signature?.publicKey}-${nearNonce?.toString("base64")}`
                : "",
            signature:
              chainName?.toLowerCase() === "solana"
                ? bs58.encode(signature[0]?.signature)
                : chainName?.toLowerCase() === "near"
                  ? signature?.signature
                  : signature,
            nonce: _signature ? _nonce : kycChallenge?.nonce,
            chainId:
              chainName?.toLowerCase() === "solana"
                ? 394
                : chainName?.toLowerCase() === "near"
                  ? 394
                  : chainId,
            chainName,
          },
        })
          .then((res) => {
            if (res?.data?.kycData) {
              return res?.data?.kycData;
            } else {
              return null;
            }
          })
          .catch((err) => {
            console.log(
              "decrypting kyc data failed with error: ",
              err?.message,
            );
            return null;
          });
      }
    }

    return null;
  } catch (err) {
    return null;
  }
};

export const saveUserKycQuestionaire = async (
  userAddress,
  chainId,
  chainName,
  signer,
  provider,
  kycData,
  userId,
  deleteNonce,
  retSignature,
) => {
  try {
    const kycChallenge = await getKycChallenge(userAddress);
    if (kycChallenge?.message && kycChallenge?.nonce) {
      const message = kycChallenge?.message;
      const nearNonce = Buffer.from(
        window.crypto.getRandomValues(new Uint8Array(32)),
      );

      const signature =
        chainName?.toLowerCase() === "solana"
          ? await signer?.signMessage({
              message: new TextEncoder().encode(message),
              account: signer?.account,
            })
          : chainName?.toLowerCase() === "near"
            ? await signer?.signMessage({
                message,
                nonce: nearNonce,
                recipient: userAddress,
              })
            : await signer.signMessage(message);

      if (signature) {
        return await axios({
          method: "POST",
          url: emailSenderUrl,
          data: {
            type: "save_kyc",
            walletAddress: userAddress,
            publicKey:
              chainName?.toLowerCase() === "near"
                ? `${signature?.publicKey}-${nearNonce?.toString("base64")}`
                : "",
            signature:
              chainName?.toLowerCase() === "solana"
                ? bs58.encode(signature[0]?.signature)
                : chainName?.toLowerCase() === "near"
                  ? signature?.signature
                  : signature,
            nonce: kycChallenge?.nonce,
            chainId:
              chainName?.toLowerCase() === "solana"
                ? 394
                : chainName?.toLowerCase() === "near"
                  ? 394
                  : chainId,
            chainName,
            kycData,
            userId,
            deleteNonce,
          },
        })
          .then((res) => {
            if (res?.data?.saved) {
              return retSignature
                ? {
                    saved: true,
                    signature,
                    nonce: kycChallenge?.nonce,
                    nearNonce,
                  }
                : true;
            } else {
              return retSignature
                ? {
                    saved: false,
                    signature: "",
                    nonce: "",
                    nearNonce: "",
                  }
                : false;
            }
          })
          .catch((err) => {
            console.log("save kyc data failed with error: ", err?.message);
            return retSignature
              ? {
                  saved: false,
                  signature: "",
                  nonce: "",
                  nearNonce: "",
                }
              : false;
          });
      }
    }

    return retSignature
      ? {
          saved: false,
          signature: "",
          nonce: "",
          nearNonce: "",
        }
      : false;
  } catch (err) {
    console.error("save ky error: ", err?.message);
    return retSignature
      ? {
          saved: false,
          signature: "",
          nonce: "",
          nearNonce: "",
        }
      : false;
  }
};

export const getRootDomain = () => {
  const hostname = window.location.hostname;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "localhost";
  }

  const parts = hostname.split(".");

  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }

  return hostname;
};
